import { useIsMounted } from '@/hooks/useIsMounted';
import { useGSAP } from '@gsap/react';
import { gsap } from 'gsap';
import Image from 'next/image';
import { createPortal } from 'react-dom';
import { useEffect, useRef } from 'react';

export type FeatureModalImage = {
  alt: string;
  height: number;
  src: string;
  width: number;
};

type FeaturesImageModalProps = {
  image: FeatureModalImage | null;
  isOpen: boolean;
  onClose: () => void;
};

export default function FeaturesImageModal({ image, isOpen, onClose }: FeaturesImageModalProps) {
  const isMounted = useIsMounted();
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);

    return () => {
      document.body.style.overflow = originalOverflow;
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  useGSAP(
    () => {
      if (!isOpen || !overlayRef.current || !contentRef.current) return;

      const tl = gsap.timeline({
        defaults: { ease: 'power2.out' },
      });

      tl.fromTo(overlayRef.current, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.2 }).fromTo(
        contentRef.current,
        { autoAlpha: 0, scale: 0.96, y: 16 },
        { autoAlpha: 1, duration: 0.3, scale: 1, y: 0 },
        '<+0.05',
      );
    },
    { dependencies: [isOpen] },
  );

  if (!isMounted || !isOpen || !image) return null;

  return createPortal(
    <div
      ref={overlayRef}
      aria-modal="true"
      className="fixed inset-0 z-300 flex items-center justify-center bg-gray-400/75 p-5 md:p-0"
      role="dialog"
      onClick={onClose}
    >
      <button
        aria-label="Fermer la modale"
        className="text-orange absolute top-24 right-5 z-91 inline-flex h-12 w-12 cursor-pointer items-center justify-center rounded-full bg-white shadow-md transition-transform duration-200 hover:scale-105 lg:top-20"
        type="button"
        onClick={onClose}
      >
        <svg
          aria-hidden="true"
          className="h-5 w-5"
          fill="none"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M6 6L18 18M18 6L6 18"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
          />
        </svg>
      </button>

      <div
        ref={contentRef}
        className="w-full md:w-3/4"
        onClick={(event) => event.stopPropagation()}
      >
        <Image
          alt={image.alt}
          className="w-full rounded-(--radius-current)"
          height={image.height}
          src={image.src}
          width={image.width}
        />
      </div>
    </div>,
    document.body,
  );
}
